import { useState } from "react"
import { toast } from "sonner"

import AptLinkCard from "@/routes/select/apt/_components/apt-link-card"
import { addAptToGroupAPI, createAptGroupAPI, removeAptFromGroupAPI } from "@/apis/apt-groups-api"

import { langStore } from "@/stores/lang-store"
import { aptSelectBundle } from "../apt-select-const"
import { aptGroupStore } from "./apt-group-store"
import { useToggleStore } from "@/hooks/use-toggle-store"
import { FAVORITE_GROUP_NAME, GROUP_COLOR_MAP } from "./apt-group-const"

const TEXT = {
  ko: {
    selectGroup: "먼저 편집할 그룹을 선택해주세요.",
    updateError: "그룹 아파트 목록을 변경하지 못했습니다."
  },
  en: {
    selectGroup: "Please select a group to edit first.",
    updateError: "Failed to update the group's apartments."
  },
  es: {
    selectGroup: "Selecciona primero un grupo para editar.",
    updateError: "No se pudieron actualizar los apartamentos del grupo."
  }
}

const ResultView = ({ aptList }) => {
  const { lang } = langStore()
  const text = TEXT[lang] ?? TEXT.ko
  const { groups, setGroups, tempGroups, updateTempGroupCodes, activeGroupName } = aptGroupStore()

  const [pendingCodes, setPendingCodes] = useState([])
  const { show: isEditMode } = useToggleStore("group-edit-mode")

  const handleToggleGroup = async (kaptCode) => {
    if (isEditMode && !activeGroupName) {
      toast.warning(text.selectGroup)
      return
    }

    if (pendingCodes.includes(kaptCode)) return

    // 편집 모드에서는 임시 상태만 변경
    if (isEditMode) {
      const group = tempGroups.find((item) => item.group_name === activeGroupName)

      if (!group || group.group_name === FAVORITE_GROUP_NAME) return

      const codes = group.codes ?? []
      const nextCodes = codes.includes(kaptCode)
        ? codes.filter((code) => code !== kaptCode)
        : [...codes, kaptCode]

      updateTempGroupCodes(group.id, nextCodes)
      return
    }

    // 일반 모드에서는 즐겨찾기에 즉시 저장
    setPendingCodes((prev) => [...prev, kaptCode])

    try {
      let group = groups.find((item) => item.group_name === FAVORITE_GROUP_NAME)

      if (!group) {
        group = await createAptGroupAPI({
          group_name: FAVORITE_GROUP_NAME,
          color_key: "yellow"
        })

        setGroups([...groups, group])
      }

      const codes = group.codes ?? []
      const isInGroup = codes.includes(kaptCode)

      if (isInGroup) {
        await removeAptFromGroupAPI({
          group_id: group.id,
          kapt_code: kaptCode
        })
      } else {
        await addAptToGroupAPI({
          group_id: group.id,
          kapt_code: kaptCode
        })
      }

      setGroups((currentGroups) =>
        currentGroups.map((item) => {
          if (item.id !== group.id) return item

          const currentCodes = item.codes ?? []

          return {
            ...item,
            codes: isInGroup
              ? currentCodes.filter((code) => code !== kaptCode)
              : [...currentCodes, kaptCode]
          }
        })
      )
    } catch (error) {
      console.error(error)
      toast.error(text.updateError)
    } finally {
      setPendingCodes((prev) => prev.filter((code) => code !== kaptCode))
    }
  }

  const starColorClass = (kaptCode) => {
    const targetGroups = isEditMode ? tempGroups : groups

    const targetGroup = activeGroupName
      ? targetGroups.find((group) => group.group_name === activeGroupName)
      : groups.find((group) => group.group_name === FAVORITE_GROUP_NAME)

    if ((targetGroup?.codes ?? []).includes(kaptCode)) {
      return GROUP_COLOR_MAP[targetGroup.color_key ?? "yellow"]?.starColor ?? "text-yellow-400"
    }

    return "text-slate-300 group-hover:text-slate-400"
  }

  return (
    <div className="grid grid-cols-1 gap-4 pb-20 md:grid-cols-2 lg:gap-6 xl:grid-cols-3">
      {aptList.length > 0 &&
        aptList.map((apt) => (
          <AptLinkCard
            key={apt.kapt_code}
            item={apt}
            isEditMode={isEditMode}
            starColor={starColorClass(apt.kapt_code)}
            onToggleGroup={handleToggleGroup}
            isPending={pendingCodes.includes(apt.kapt_code)}
          />
        ))}

      {aptList.length === 0 && (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-20 text-center md:col-span-2 xl:col-span-3">
          <span className="mb-4 block text-4xl">🔍</span>
          <p className="text-lg font-medium text-slate-500">
            {aptSelectBundle.empty.defaultTitle[lang]}
          </p>
        </div>
      )}
    </div>
  )
}

export default ResultView
